import { useCallback, useEffect, useMemo, useState } from 'react';

import * as EventEmitter from '@/utils/event-emitter';
import randomString from '@/utils/random-string';

/**
 * Companion hook for the Popover component — mirrors the useFixedDrawer pattern.
 *
 * Usage:
 *   const { open, id, setOpenPopover, setClosePopover, setTogglePopover } = usePopover();
 *
 *   <Popover id={id} ... />
 *
 * Notes:
 * - setOpenPopover / setClosePopover / setTogglePopover broadcast events that the Popover
 *   component listens to and handles internally — same pattern as useFixedDrawer.
 * - `open` is synced back from the Popover's own broadcast (`${id}::popover-open`), which
 *   fires true only when the popover is fully visible (display === 'block', not closing).
 * - Do NOT pass `open` as a controlled prop to the Popover when using this hook — events
 *   handle open/close instead.
 */
const usePopover = (params?: { id?: string }) => {
  const { id: propsId } = params || {};

  const id = useMemo(() => {
    return propsId || randomString(7);
  }, [propsId]);

  const [open, setOpen] = useState(false);

  const setOpenPopover = useCallback(() => {
    EventEmitter.broadcastEvent(`${id}::popover-open-request`);
  }, [id]);

  const setClosePopover = useCallback(() => {
    EventEmitter.broadcastEvent(`${id}::popover-close-request`);
  }, [id]);

  const setTogglePopover = useCallback(() => {
    EventEmitter.broadcastEvent(`${id}::popover-toggle-request`);
  }, [id]);

  // Sync open state from the Popover's own broadcast.
  useEffect(() => {
    const handleOpenStateChange = (val: boolean) => setOpen(val);
    EventEmitter.addEventListener(`${id}::popover-open`, handleOpenStateChange);
    return () => EventEmitter.removeEventListener(`${id}::popover-open`, handleOpenStateChange);
  }, [id]);

  return useMemo(
    () => ({
      open,
      id,
      setOpenPopover,
      setClosePopover,
      setTogglePopover,
    }),
    [id, open, setClosePopover, setOpenPopover, setTogglePopover]
  );
};

export default usePopover;
